import { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import financeApiClient from '@/api/financeClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Try to get user from localStorage
    const userData = localStorage.getItem('userData');
    const financeUser = localStorage.getItem('financeUser');
    
    if (financeUser) {
      setUser(JSON.parse(financeUser));
    } else if (userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await financeApiClient.post('/auth/login', { email, password });
      const { token, user: userData } = response.data;
      localStorage.setItem('financeToken', token);
      localStorage.setItem('financeUser', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('financeToken');
    localStorage.removeItem('financeUser');
    setUser(null);
    navigate('/portal');
  };

  const hasRole = (roles) => {
    if (!user) return false;
    const roleArray = Array.isArray(roles) ? roles : [roles];
    return roleArray.includes(user.role);
  };

  const isAdmin = () => {
    return hasRole(['super_admin', 'platform_admin', 'finance_admin']);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    hasRole,
    isAdmin,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    // Return default values if not wrapped in provider
    const userData = localStorage.getItem('userData');
    const financeUser = localStorage.getItem('financeUser');
    const user = financeUser ? JSON.parse(financeUser) : (userData ? JSON.parse(userData) : null);
    
    return {
      user,
      loading: false,
      login: async () => {},
      logout: () => {},
      hasRole: () => true,
      isAdmin: () => true,
      isAuthenticated: !!user,
    };
  }
  return context;
}

export default AuthContext;
